// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useEffect, useRef, useState } from "react";
import { Telemetry } from "telemetry/TelemetryManager";

export enum RequestState {
    None,
    InProgress,
    Complete,
    Error
}

export interface AsyncHelper<T> {
    execute: (promise: Promise<T>) => Promise<T | null>;
    state: RequestState;
    data?: T;
    error?: Error;
    onComplete?: () => void;
}

interface AsyncState<T> {
    state: RequestState,
    error?: Error,
    data?: T
};

/**
 * Hook that helps when using an async function in a component.
 * 1. Executes the async function.
 * 2. Ensures the component is still loaded upon completion.
 * 3. Returns state, data, and error information for the request.
 * @param friendlyName
 */
export function useAsyncHelper<T>(friendlyName: string, token?: any, onComplete?: (result?: T, error?: any) => void): AsyncHelper<T> {
    const [state, setState] = useState<AsyncState<T>>({ state: RequestState.None });
    const completeRef = useRef(onComplete);

    const isLoadedRef = useRef(true);

    useEffect(() => {
        return () => {
            isLoadedRef.current = false;
        }
    }, []);

    useEffect(() => {
        completeRef.current = onComplete;
    }, [onComplete])

    useEffect(() => {
        if (token === undefined) {
            return;
        }

        setState({ state: RequestState.None });
    }, [token]);

    const execute = useCallback(async (promise: Promise<T>): Promise<T | null> => {
        if (state.state === RequestState.InProgress) {
            return null;
        }

        const curToken = token;
        async function executeAsync(promise: Promise<T>): Promise<T | null> {
            try {
                setState(x => ({
                    ...x,
                    state: RequestState.InProgress
                }));

                const result = await promise;
                if (!isLoadedRef.current || curToken !== token) {
                    return null;
                }

                setState(x => ({
                    ...x,
                    state: RequestState.Complete,
                    data: result
                }));

                completeRef.current?.(result);
                return result;
            }
            catch (error) {
                if (!isLoadedRef.current || curToken !== token) {
                    return null;
                }

                setState(x => ({
                    ...x,
                    state: RequestState.Error,
                    error: error as Error
                }));

                Telemetry.trackException(error as Error, { friendlyName: friendlyName })
                completeRef.current?.(undefined, error);
            }

            return null;
        }

        return await executeAsync(promise);
    },
        [state.state, token, friendlyName]);

    return {
        execute: execute,
        ...state
    };
}
